import { useEntityChange } from '@/common/entityChange/EntityChangeContext';
import { Mode } from '@/common/entityFramework/enumerations/Mode';
import { useNavState } from '@/common/navState/NavStateContext';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Menu, MenuItem, Select, Tooltip, Typography, useColorScheme } from '@mui/material';
import { usePrincipal } from '../../principal/PrincipalContext';

export const ProfileMenu = ({ anchorEl, open, onClose }) => {
  const { principal, org, logout, changeOrg, hasRole } = usePrincipal();
  const { navigateTo } = useNavState();
  const rsrc = useRsrc();

  const { blockRefreshTrigger, toggleBlockRefreshTrigger } = useEntityChange();
  const { mode, setMode } = useColorScheme({ initialMode: 'light' });

  const canOpenUserDetail = hasRole('ADMIN_USER_VIEW');

  // The principal carries the username, not the USER entity id the detail route needs
  const openUserDetail = () => {
    rsrc.one('admin', 'USER', { expr: ExprUtils.whereParams('username', principal.username) }).then((user) => {
      if (!user) return;
      onClose();
      navigateTo('/admin/user', { mode: Mode.DETAIL, entityId: user.id });
    });
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      slotProps={{ paper: { sx: { width: 280 } } }}
    >
      <Box onClick={(e) => e.stopPropagation()} sx={{ p: 2 }}>
        <Tooltip title={canOpenUserDetail ? 'Open my user profile' : ''} placement="right">
          <MenuItem
            onClick={canOpenUserDetail ? openUserDetail : undefined}
            disabled={!canOpenUserDetail}
            data-test-id="profile-user-detail"
            sx={{ borderRadius: (theme) => theme.shape.borderRadius, mb: 2, px: 1, '&.Mui-disabled': { opacity: 1 } }}
          >
            <Box
              component="img"
              src={principal.picture}
              alt="Profile"
              sx={{ width: 40, height: 40, borderRadius: '50%', mr: 2 }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" noWrap>
                {principal.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                {principal.username}
              </Typography>
            </Box>
          </MenuItem>
        </Tooltip>
        <Box>
          {principal.orgs && principal.orgs.length > 0 && (
            <MenuItem disableRipple sx={{ borderRadius: (theme) => theme.shape.borderRadius }}>
              <FontAwesomeIcon icon={fas.faGlobe} style={{ marginRight: 8 }} />
              <Select
                value={org}
                onChange={(e) => changeOrg(e.target.value)}
                variant="standard"
                disableUnderline
                sx={{
                  padding: 0,
                  minWidth: 0,
                }}
              >
                {principal.orgs.map((org) => (
                  <MenuItem key={org} value={org}>
                    {org}
                  </MenuItem>
                ))}
              </Select>
            </MenuItem>
          )}
        </Box>

        <MenuItem
          onClick={() => {
            console.log(mode);
            mode === 'dark' ? setMode('light') : setMode('dark');
          }}
          sx={{ borderRadius: (theme) => theme.shape.borderRadius }}
        >
          <FontAwesomeIcon icon={mode === 'dark' ? fas.faSun : fas.faMoon} style={{ marginRight: 8 }} />
          {mode === 'dark' ? 'Light Mode' : 'Dark Mode'}
        </MenuItem>
        <MenuItem onClick={toggleBlockRefreshTrigger} sx={{ borderRadius: (theme) => theme.shape.borderRadius }}>
          <FontAwesomeIcon icon={blockRefreshTrigger ? fas.faRotate : fas.faBan} style={{ marginRight: 8 }} />
          {blockRefreshTrigger ? 'Enable Auto Refresh' : 'Disable Auto Refresh'}
        </MenuItem>
        <MenuItem
          data-test-id="logout-button"
          onClick={logout}
          sx={{ borderRadius: (theme) => theme.shape.borderRadius }}
        >
          <FontAwesomeIcon icon={fas.faSignOutAlt} style={{ marginRight: 8 }} />
          Logout
        </MenuItem>
      </Box>
    </Menu>
  );
};
